import { useState, useMemo } from "react";
import { LinkContainer } from "react-router-bootstrap";
import { Link } from "react-router-dom";
import { Table, Button, Container, Row, Col, Form } from "react-bootstrap";
import {
  FaTrash,
  FaEdit,
  FaPlus,
  FaCalendarAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import {
  useGetAllEventsQuery,
  useDeleteEventMutation,
} from "../../slices/eventApiSlice";
import ErrorBoundary from "../../components/ErrorBoundary";
import moment from "moment-timezone";
import ConfirmModal from "../../components/ConfirmModal";

const CLUB_TZ = "America/New_York";

// Filter Controls Component
const FilterControls = ({ filters, onFilterChange }) => (
  <Row className="my-3 g-3">
    <Col md={6}>
      <Form.Group>
        <Form.Label>Event Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Filter by Event Name"
          value={filters.title}
          onChange={(e) => onFilterChange("title", e.target.value)}
        />
      </Form.Group>
    </Col>
    <Col md={3}>
      <Form.Group>
        <Form.Label>Start Date</Form.Label>
        <Form.Control
          type="date"
          value={filters.startDate}
          onChange={(e) => onFilterChange("startDate", e.target.value)}
        />
      </Form.Group>
    </Col>
    <Col md={3}>
      <Form.Group>
        <Form.Label>End Date</Form.Label>
        <Form.Control
          type="date"
          value={filters.endDate}
          onChange={(e) => onFilterChange("endDate", e.target.value)}
        />
      </Form.Group>
    </Col>
  </Row>
);

FilterControls.propTypes = {
  filters: PropTypes.shape({
    title: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

// Event Table Row Component
const EventTableRow = ({ event, onDelete }) => {
  const formatDate = (isoString) => {
    const m = moment(isoString).tz(CLUB_TZ);
    return event.allDay
      ? m.format("MMM D, YYYY")
      : m.format("MMM D, YYYY, h:mm A");
  };

  return (
    <tr>
      <td className="align-middle">
        <div className="d-flex align-items-center">
          <FaCalendarAlt className="text-primary me-2" />
          <span>{event.title}</span>
        </div>
      </td>
      <td className="align-middle">{formatDate(event.start)}</td>
      <td className="align-middle">{formatDate(event.end)}</td>
      <td className="align-middle">
        <div className="d-flex justify-content-center gap-2">
          <LinkContainer to={`/admin/events/${event._id}/edit`}>
            <Button variant="outline-primary" size="sm">
              <FaEdit className="me-1" /> Edit
            </Button>
          </LinkContainer>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDelete(event._id)}
          >
            <FaTrash className="me-1" /> Delete
          </Button>
        </div>
      </td>
    </tr>
  );
};

EventTableRow.propTypes = {
  event: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    allDay: PropTypes.bool.isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

const EventListScreen = () => {
  const { data: events, refetch, isLoading, error } = useGetAllEventsQuery();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [confirmState, setConfirmState] = useState({ show: false, id: null });

  const [filters, setFilters] = useState({
    title: "",
    startDate: "",
    endDate: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "start",
    direction: "asc",
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const deleteHandler = async (id) => {
    try {
      await deleteEvent(id).unwrap();
      toast.success("Event deleted successfully");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const openConfirm = (id) => setConfirmState({ show: true, id });
  const closeConfirm = () => setConfirmState({ show: false, id: null });
  const confirmDelete = () => {
    const { id } = confirmState;
    closeConfirm();
    if (id) {
      deleteHandler(id);
    }
  };

  const filteredEvents = useMemo(() => {
    if (!events) return [];

    const startBoundary = filters.startDate
      ? moment
          .tz(filters.startDate, "YYYY-MM-DD", CLUB_TZ)
          .startOf("day")
          .toDate()
      : null;
    const endBoundary = filters.endDate
      ? moment.tz(filters.endDate, "YYYY-MM-DD", CLUB_TZ).endOf("day").toDate()
      : null;

    return events.filter((event) => {
      const matchesTitle = event.title
        .toLowerCase()
        .includes(filters.title.toLowerCase());

      const evStart = new Date(event.start);
      const evEnd = new Date(event.end);

      const withinStartDate = !startBoundary || evStart >= startBoundary;
      const withinEndDate = !endBoundary || evEnd <= endBoundary;

      return matchesTitle && withinStartDate && withinEndDate;
    });
  }, [events, filters]);

  const sortedEvents = useMemo(() => {
    const sorted = [...filteredEvents];
    sorted.sort((a, b) => {
      if (sortConfig.key === "title") {
        return a.title.localeCompare(b.title);
      }
      if (sortConfig.key === "start") {
        return new Date(a.start) - new Date(b.start);
      }
      if (sortConfig.key === "end") {
        return new Date(a.end) - new Date(b.end);
      }
      return 0;
    });
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [filteredEvents, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ms-1" />
    ) : (
      <FaSortDown className="ms-1" />
    );
  };

  if (isLoading || isDeleting) return <Loader />;
  if (error)
    return (
      <Message variant="danger">
        {error?.data?.message || "Error loading events"}
      </Message>
    );

  return (
    <ErrorBoundary>
      <Container className="py-4">
        <Row className="align-items-center mb-4">
          <Col>
            <h1 className="mb-0">Event Management</h1>
          </Col>
          <Col xs="auto">
            <Link to="/admin/events/create" className="btn btn-primary">
              <FaPlus className="me-1" /> Add Event
            </Link>
          </Col>
        </Row>

        <FilterControls filters={filters} onFilterChange={handleFilterChange} />

        <div className="table-responsive">
          <Table hover className="align-middle bg-white shadow-sm rounded">
            <thead className="bg-light">
              <tr>
                <th>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-body fw-bold"
                    onClick={() => handleSort("title")}
                  >
                    Event Name {renderSortIcon("title")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-body fw-bold"
                    onClick={() => handleSort("start")}
                  >
                    Start Date {renderSortIcon("start")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-body fw-bold"
                    onClick={() => handleSort("end")}
                  >
                    End Date {renderSortIcon("end")}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map((event) => (
                <EventTableRow
                  key={event._id}
                  event={event}
                  onDelete={openConfirm}
                />
              ))}
              {sortedEvents.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    No events found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <ConfirmModal
          show={confirmState.show}
          title="Delete Event"
          body="Are you sure you want to delete this event?"
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={confirmDelete}
          onCancel={closeConfirm}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default EventListScreen;
